import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { ArrowRight, CheckCircle2, TrendingUp } from 'lucide-react';
import { servicesSeedData } from '../../database/seedData/services';
import EngagementTriggerMap from '../../components/EngagementTriggerMap';
import ExecutiveSummary from '../../components/ExecutiveSummary';
import {
  AuthorityTriadDiagram,
  ExecutiveFlowRail,
  ExecutiveSplitSection,
} from '../../components/executive';

const agenticEngagementLanes = (useCases) => [
  {
    label: 'Oversight & Liability',
    description: 'When authority, incidents, or legal exposure need a reset.',
    icon: 'scale',
    triggers: [useCases[0], useCases[2], useCases[4]].filter(Boolean),
  },
  {
    label: 'Operating Risk',
    description: 'When automation pressure outruns governance readiness.',
    icon: 'shield',
    triggers: [useCases[1]].filter(Boolean),
  },
  {
    label: 'Capital & Commitment',
    description: 'When vendor or platform decisions are about to harden.',
    icon: 'wallet',
    triggers: [useCases[3], useCases[5]].filter(Boolean),
  },
];

const AgenticAI = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const service = servicesSeedData.find(s => s.slug === 'agentic-ai-advisory');

  if (!service) {
    return (
      <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950' : 'bg-white'} py-24`}>
        <div className="max-w-4xl mx-auto px-8 text-center">
          <h1 className={`text-5xl font-bold mb-8 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Service Not Found
          </h1>
          <button
            onClick={() => navigate('/services')}
            className={`px-10 py-4 rounded-xl text-xl font-semibold ${theme === 'dark' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-gray-900 hover:bg-gray-800 text-white'}`}
          >
            View All Services
          </button>
        </div>
      </div>
    );
  }

  const phases = service.timeline
    ? [
        { title: 'Assess', description: service.timeline.discovery },
        { title: 'Govern', description: service.timeline.design || service.timeline.pilot || service.timeline.strategy },
        { title: 'Test & Decide', description: service.timeline.production || service.timeline.implementation },
      ].filter((entry) => entry.description)
    : [];

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950' : 'bg-white'}`}>

      {/* Hero */}
      <div className={`relative ${theme === 'dark' ? 'bg-slate-900' : 'bg-gray-900'} overflow-hidden`}>
        <div className="absolute left-0 top-0 bottom-0 w-2 bg-gradient-to-b from-indigo-400 via-violet-500 to-indigo-600"></div>

        <div className="max-w-7xl mx-auto px-8 py-24 lg:py-32">
          <div className="flex justify-between items-center mb-12">
            <button
              onClick={() => navigate('/services')}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-base font-semibold bg-white/10 text-white border border-white/30 hover:bg-white/20 transition-colors"
            >
              <ArrowRight className="w-4 h-4 rotate-180" />
              Back to Services
            </button>
            <span className="text-sm uppercase tracking-[0.4em] text-indigo-300/80">
              Shared Oxygen Advisory
            </span>
          </div>
          <div className="max-w-4xl">
            <div className="flex items-center mb-6">
              <div className="h-px w-12 bg-indigo-400 mr-4"></div>
              <span className="text-indigo-300 uppercase tracking-widest text-sm font-semibold">
                Agentic Systems
              </span>
            </div>

            <h1 className="text-5xl lg:text-7xl font-bold text-white mb-8 leading-tight">
              {service.title}
            </h1>

            <p className="text-2xl lg:text-3xl text-gray-200 font-normal leading-relaxed mb-12">
              {service.shortDescription}
            </p>

            <button
              onClick={() => navigate('/contact')}
              className="group inline-flex items-center px-12 py-5 bg-indigo-500 hover:bg-indigo-600 text-white text-lg font-bold rounded-none transition-all duration-300"
            >
              Request a Conversation
              <ArrowRight className="ml-3 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </div>

      {/* Executive Summary */}
      <div className={`${theme === 'dark' ? 'bg-slate-950' : 'bg-white'} py-20`}>
        <div className="max-w-6xl mx-auto px-8">
          <ExecutiveSummary theme={theme} variant="indigo">
            {service.description}
          </ExecutiveSummary>
        </div>
      </div>

      {/* Leadership context */}
      <div className={`py-20 ${theme === 'dark' ? 'bg-slate-900/30' : 'bg-gray-50'}`}>
        <div className="max-w-6xl mx-auto px-8">
          <ExecutiveSplitSection
            theme={theme}
            accentBar="bg-indigo-500"
            title="Why This Requires Executive Attention"
            illustration={<AuthorityTriadDiagram theme={theme} />}
          >
            <p>
              Assistants and copilots stay in the advisory lane—they suggest, summarize, draft. Agentic systems
              cross into execution: they call APIs, update ledgers, trigger downstream workflows, and commit
              organizational resources. Once authority is delegated, the question is no longer whether the model
              is accurate—it is whether the organization can explain, audit, and reverse what it did.
            </p>
            <p>
              We provide independent counsel at the executive level—on authority, oversight, vendor and
              architecture choices, and the trade-offs that come with delegating action to software. We do
              not build agents, resell platforms, or embed for implementation. Our role is to strengthen how
              leadership thinks about autonomous systems, not to prescribe a fixed package of work.
            </p>
          </ExecutiveSplitSection>
        </div>
      </div>

      {/* When counsel is sought */}
      <div className={`py-20 ${theme === 'dark' ? 'bg-slate-950' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-8">
          <div className="mb-14">
            <div className="h-1 w-20 bg-indigo-500 mb-6"></div>
            <h2 className={`text-4xl lg:text-5xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              When Organizations Seek Counsel
            </h2>
            <p className={`mt-4 text-lg max-w-3xl ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
              Situations where an independent perspective helps before authority, contracts, or organizational
              commitments harden.
            </p>
          </div>

          <EngagementTriggerMap
            lanes={agenticEngagementLanes(service.useCases)}
            theme={theme}
            variant="indigo"
            hubLabel="Where judgment is needed"
          />
        </div>
      </div>

      {/* Advisory focus */}
      <div className={`py-20 ${theme === 'dark' ? 'bg-slate-900/30' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-16">

            <div>
              <div className="mb-8">
                <div className="h-1 w-20 bg-indigo-500 mb-6"></div>
                <h3 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Where We Advise
                </h3>
                <p className={`mt-4 text-lg ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
                  Questions we help leadership work through—not a catalog of predetermined outputs.
                </p>
              </div>
              <div className="space-y-6">
                {service.keyFeatures.map((feature, i) => (
                  <div key={i} className="flex items-start group">
                    <CheckCircle2 className={`w-6 h-6 mr-4 mt-1 flex-shrink-0 ${theme === 'dark' ? 'text-indigo-400' : 'text-indigo-600'}`} />
                    <span className={`text-lg leading-relaxed ${theme === 'dark' ? 'text-slate-300 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'} transition-colors`}>
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="mb-8">
                <div className="h-1 w-20 bg-emerald-500 mb-6"></div>
                <h3 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  What Shifts
                </h3>
                <p className={`mt-4 text-lg ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
                  How counsel changes the quality of executive judgment—not a promise of specific results.
                </p>
              </div>
              <div className="space-y-6">
                {service.benefits.map((benefit, i) => (
                  <div key={i} className="flex items-start group">
                    <TrendingUp className={`w-6 h-6 mr-4 mt-1 flex-shrink-0 ${theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'}`} />
                    <span className={`text-lg leading-relaxed ${theme === 'dark' ? 'text-slate-300 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'} transition-colors`}>
                      {benefit}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Approach */}
      {phases.length > 0 && (
        <div className={`py-20 ${theme === 'dark' ? 'bg-slate-900/40' : 'bg-gray-50'}`}>
          <div className="max-w-6xl mx-auto px-8">
            <div className="text-center mb-12">
              <div className="h-1 w-16 bg-indigo-500 mx-auto mb-6"></div>
              <h2 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                How We Work
              </h2>
              <p className={`mt-4 text-lg max-w-3xl mx-auto ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
                A natural sequence for advisory—not a fixed engagement model. Depth and pace follow the
                decisions in front of you and how your organization already governs systems that act on its behalf.
              </p>
            </div>
            <ExecutiveFlowRail phases={phases} theme={theme} variant="indigo" />
          </div>
        </div>
      )}

      {/* CTA */}
      <div className={`py-24 ${theme === 'dark' ? 'bg-slate-900' : 'bg-gray-900'}`}>
        <div className="max-w-5xl mx-auto px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            Discuss Agentic AI Priorities
          </h2>
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            An initial conversation to understand your situation, constraints, and what kind of counsel would be useful.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <button
              onClick={() => navigate('/contact')}
              className="px-16 py-6 bg-indigo-500 hover:bg-indigo-600 text-white text-xl font-bold transition-all duration-300"
            >
              Contact Us
            </button>
            <button
              onClick={() => navigate('/services')}
              className="px-16 py-6 border-2 border-white text-white hover:bg-white hover:text-gray-900 text-xl font-bold transition-all duration-300"
            >
              Explore All Services
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgenticAI;
